/**
 * terminalKeyHandler — Shift+Enter newline interception (E.21.24).
 *
 * xterm.js by default sends `\r` (0x0d, CR) for both Enter and Shift+Enter
 * — there's no native way to distinguish them at the pty layer. This module
 * provides a custom-key-event handler that intercepts Shift+Enter on the
 * keydown phase and emits `\n` (0x0a, LF) instead.
 *
 * Why 0x0a: it's the common-denominator "newline-without-submit" byte that
 *   - hermes binds to `c-j` (cli.py:9273)
 *
 * Coverage: hermes. (goose bound Ctrl+J via rustyline, input.rs:137-143, but
 * goose was deprecated 2026-10-21 and is no longer offered.)
 * Out of scope: opencode (upstream `input_newline` config exists but handler
 * not wired) and openclaw shell (bash readline; no compose-then-submit
 * semantic).
 */

/**
 * True iff the event is a Shift+Enter keydown that should be intercepted.
 *
 * - `key === 'Enter'` matches both Enter and Numpad Enter.
 * - `shiftKey === true` distinguishes from plain Enter (which the upstream
 *   xterm encoding pipeline correctly maps to `\r` for submit).
 * - Other modifiers (ctrl, alt, meta) are excluded — Ctrl+Enter / Alt+Enter
 *   already have terminal-native escape sequences (some tools depend on
 *   them); only Shift+Enter is the unclaimed combo.
 */
export function isShiftEnterKeydown(event: KeyboardEvent): boolean {
  return (
    event.type === 'keydown' &&
    event.key === 'Enter' &&
    event.shiftKey === true &&
    !event.ctrlKey &&
    !event.altKey &&
    !event.metaKey
  );
}

/**
 * The byte we write to the pty for Shift+Enter (LF, 0x0a).
 *
 * Not `\r\n` — that would inject CR+LF, and prompt_toolkit/rustyline/ink-based
 * REPLs all interpret CR (0x0d) as submit. Pure LF (0x0a) is the
 * newline-without-submit signal across all 3 supported CLIs.
 */
export const SHIFT_ENTER_BYTES = '\n';
